"use client";

import { Search, Filter } from "lucide-react";

interface DocumentsTableMiniWidgetProps {
  className?: string;
  style?: React.CSSProperties;
}

// Document type badge colors from the actual codebase (document.ts DOCUMENT_TYPE_BADGE_CONFIG)
const documentTypeBadgeConfig: Record<string, { label: string; color: string }> = {
  tax_id_certificate: { label: "CIF/NIF", color: "bg-purple-50 text-purple-700" },
  bank_account_certificate: { label: "Cert. Bancario", color: "bg-blue-50 text-blue-700" },
  inaem_certificate: { label: "Cert. INAEM", color: "bg-green-50 text-green-700" },
  articles_of_association: { label: "Estatutos", color: "bg-amber-50 text-amber-700" },
};

// Mock document data - realistic Spanish document names
const mockDocuments = [
  {
    id: "1",
    name: "CIF Representante Legal",
    type: "tax_id_certificate",
    extension: "PDF",
    uploadedAt: "15 ene 2025",
  },
  {
    id: "2",
    name: "Certificado Bancario BBVA",
    type: "bank_account_certificate",
    extension: "JPEG",
    uploadedAt: "12 ene 2025",
  },
  {
    id: "3",
    name: "Certificado INAEM 2024",
    type: "inaem_certificate",
    extension: "PDF",
    uploadedAt: "08 ene 2025",
  },
  {
    id: "4",
    name: "Estatutos Sociales",
    type: "articles_of_association",
    extension: "DOCX",
    uploadedAt: "03 ene 2025",
  },
];

export function DocumentsTableMiniWidget({ className, style }: DocumentsTableMiniWidgetProps) {
  const renderDocumentTypeBadge = (type: string) => {
    const config = documentTypeBadgeConfig[type] || {
      label: type,
      color: "bg-zinc-100 text-zinc-700",
    };
    return (
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium whitespace-nowrap ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  return (
    <div className={className} style={style}>
      {/* Card Container - DataTable style with rounded corners and border */}
      <div className="w-96 bg-white rounded-lg border border-zinc-200 overflow-hidden">
        {/* TableActionsBar - Search and Filter header */}
        <div className="px-3 py-2.5 flex items-center gap-2">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search
              className="absolute left-2 top-1/2 transform -translate-y-1/2 text-zinc-400"
              size={10}
              strokeWidth={2.5}
            />
            <input
              type="text"
              placeholder="Buscar..."
              readOnly
              autoComplete="off"
              tabIndex={-1}
              className="pl-6 pr-2 py-1 w-full text-[10px] border border-zinc-200 rounded-md bg-white shadow-sm placeholder:text-zinc-400 cursor-default"
            />
          </div>

          {/* Filter Button */}
          <button className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-zinc-700 bg-white border border-zinc-200 rounded-md shadow-sm">
            <Filter size={10} strokeWidth={2.5} />
            Filtros
          </button>
        </div>

        {/* Table with border-top separator */}
        <div className="border-t border-zinc-200">
          {/* Table Header */}
          <div className="px-3 py-2 bg-zinc-50/70 border-b border-zinc-200">
            <div className="grid grid-cols-[1fr_80px_45px_70px] gap-4 items-center">
              <span className="font-medium text-zinc-500 uppercase tracking-wider text-[9px]">
                Nombre
              </span>
              <span className="font-medium text-zinc-500 uppercase tracking-wider text-[9px] text-center">
                Tipo
              </span>
              <span className="font-medium text-zinc-500 uppercase tracking-wider text-[9px] text-center">
                Ext.
              </span>
              <span className="font-medium text-zinc-500 uppercase tracking-wider text-[9px] text-right">
                Fecha
              </span>
            </div>
          </div>

          {/* Table Body */}
          <div className="bg-white">
            {mockDocuments.map((doc, index) => (
              <div
                key={doc.id}
                className={`px-3 py-2.5 ${
                  index !== mockDocuments.length - 1 ? "border-b border-zinc-100" : ""
                }`}
              >
                <div className="grid grid-cols-[1fr_80px_45px_70px] gap-4 items-center">
                  {/* Document Name */}
                  <span className="text-[10px] text-zinc-900 font-medium truncate" title={doc.name}>
                    {doc.name}
                  </span>

                  {/* Document Type Badge */}
                  <div className="flex justify-center">{renderDocumentTypeBadge(doc.type)}</div>

                  {/* Extension */}
                  <span className="text-[10px] text-zinc-500 text-center">{doc.extension}</span>

                  {/* Upload Date */}
                  <span className="text-[10px] text-zinc-500 text-right whitespace-nowrap">
                    {doc.uploadedAt}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pagination footer */}
        <div className="px-3 py-2 border-t border-zinc-200">
          <span className="text-[9px] text-zinc-500">
            Mostrando <span className="font-medium">1</span> a{" "}
            <span className="font-medium">4</span> de <span className="font-medium">4</span>{" "}
            resultados
          </span>
        </div>
      </div>
    </div>
  );
}

export default DocumentsTableMiniWidget;
